import type { I18n } from "@lingui/core"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { ActionIcon, Badge, Button, Group, Paper, Stack, Text } from "@mantine/core"
import { ADAPTER_LABELS, type TCircuitState } from "./constants"

interface IProps {
  title: string
  description?: string
  order: string[]
  saving: boolean
  /**
   * Circuit-breaker state per provider (company mode only). `undefined` means
   * don't render health at all — the platform-admin view has no company context
   * and uses the Diagnostics page for global failover health instead. When the
   * map is present, a provider absent from it hasn't run since the last server
   * restart, so its row shows "No data yet".
   */
  health?: Record<string, TCircuitState>
  /**
   * Whether a key actually resolves per provider (org BYOK → platform → env) —
   * the same check the runtime chain resolver uses when it SILENTLY skips a
   * keyless provider. A provider mapped to `false` renders a "No key — will be
   * skipped" warning so the displayed order matches what the runtime will do.
   * `undefined` (platform-admin view / loading) hides the warnings.
   */
  resolvedKeys?: Record<string, boolean>
  onMove: (from: number, to: number) => void
  onSave: () => void
}

/**
 * Map the circuit-breaker machine state to a plain-language health badge. The
 * raw states are jargon ("closed" reads like "broken" when it means healthy),
 * so a non-engineer audience sees Healthy / Cooling down / Failing with semantic
 * tones. A provider with no state yet renders a dimmed "No data yet".
 */
function healthBadge(state: TCircuitState | undefined, i18n: I18n): React.JSX.Element {
  if (state === undefined) {
    return (
      <Text size="xs" c="dimmed">
        {i18n._(msg`No data yet`)}
      </Text>
    )
  }
  if (state === "open") {
    return (
      <Badge variant="light" color="danger" size="sm">
        {i18n._(msg`Failing`)}
      </Badge>
    )
  }
  if (state === "half-open") {
    return (
      <Badge variant="light" color="warning" size="sm">
        {i18n._(msg`Cooling down`)}
      </Badge>
    )
  }
  return (
    <Badge variant="dot" color="success" size="sm">
      {i18n._(msg`Healthy`)}
    </Badge>
  )
}

export function FallbackChainSection({
  title,
  description,
  order,
  saving,
  health,
  resolvedKeys,
  onMove,
  onSave,
}: IProps): React.JSX.Element {
  const { i18n } = useLingui()
  return (
    <Paper withBorder p="md">
      <Text fw={600} mb={description ? 2 : "sm"}>
        {title}
      </Text>
      {description && (
        <Text size="xs" c="dimmed" mb="sm">
          {description}
        </Text>
      )}
      <Stack gap="xs" mb="md">
        {order.map((adapter, index) => (
          <Group key={adapter} justify="space-between" wrap="nowrap">
            <Group gap="xs" wrap="nowrap">
              {/* Rank/priority indicator — brand chrome (not an "info" signal),
                  so it follows the theme: wine in Bordeaux, teal in Classic. */}
              <Badge variant="filled" color="brand" size="sm">
                {index + 1}
              </Badge>
              <Text size="sm">{ADAPTER_LABELS[adapter] ?? adapter}</Text>
              {index === 0 && (
                <Badge variant="light" color="brand" size="sm">
                  <Trans>Primary</Trans>
                </Badge>
              )}
              {health !== undefined && healthBadge(health[adapter], i18n)}
              {resolvedKeys?.[adapter] === false && (
                <Badge variant="light" color="warning" size="sm">
                  <Trans>No key — will be skipped</Trans>
                </Badge>
              )}
            </Group>
            <Group gap={4} wrap="nowrap">
              <ActionIcon
                variant="subtle"
                size="sm"
                disabled={index === 0}
                onClick={() => onMove(index, index - 1)}
                aria-label={i18n._(msg`Move ${ADAPTER_LABELS[adapter] ?? adapter} up`)}
              >
                ▲
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                size="sm"
                disabled={index === order.length - 1}
                onClick={() => onMove(index, index + 1)}
                aria-label={i18n._(msg`Move ${ADAPTER_LABELS[adapter] ?? adapter} down`)}
              >
                ▼
              </ActionIcon>
            </Group>
          </Group>
        ))}
      </Stack>
      <Button size="sm" onClick={onSave} loading={saving}>
        <Trans>Save</Trans>
      </Button>
    </Paper>
  )
}
